import { useEffect, useState } from "react";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { centsToDisplay } from "../lib/money.js";
import { inputCls } from "../components/ui.jsx";
import { localToday } from "../lib/dates.js";

// Where the money came from decides whether anything hits the ledger. Out of
// the business account it is an owner draw; off a personal card the business
// books never see it.
const PAID_FROM = [
  { value: "VIA Checking", label: "VIA Checking (owner draw)" },
  { value: "VIA debit card", label: "VIA debit card (owner draw)" },
  { value: "personal", label: "Personal account — no ledger entry" },
];

function GiveForm({ month, pct, onDone, onCancel }) {
  const [paidFrom, setPaidFrom] = useState(PAID_FROM[0].value);
  const [paidOn, setPaidOn] = useState(localToday());
  const [payee, setPayee] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit() {
    setBusy(true);
    setErr("");
    try {
      await api("/api/tithe", {
        method: "POST",
        body: { action: "give", month_start: month.month_start, paid_from: paidFrom, paid_on: paidOn, payee },
      });
      onDone();
    } catch (e) {
      setErr(e.message || "Could not save");
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 border-t border-slate-100 bg-slate-50 px-4 py-3">
      <p className="text-xs text-slate-500">
        Giving {centsToDisplay(month.tithe_cents)} — {pct}% of {centsToDisplay(month.base_cents)}.
      </p>
      <label className="block">
        <span className="text-xs font-medium text-slate-500">Paid from</span>
        <select value={paidFrom} onChange={(e) => setPaidFrom(e.target.value)} className={inputCls}>
          {PAID_FROM.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Date given</span>
          <input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className={inputCls} />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-500">Church / payee</span>
          <input value={payee} onChange={(e) => setPayee(e.target.value)} placeholder="Tithe" className={inputCls} />
        </label>
      </div>
      {paidFrom !== "personal" && (
        <p className="rounded-lg bg-white px-3 py-2 text-[11px] text-slate-500">
          This posts an owner withdrawal of {centsToDisplay(month.tithe_cents)} so the money leaving the account is on
          the books — never as a business expense. Give it from your personal account.
        </p>
      )}
      {err && <p className="text-xs text-red-600">{err}</p>}
      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={busy}
          className="flex-1 rounded-xl bg-navy-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? "Saving…" : "Mark given"}
        </button>
        <button onClick={onCancel} className="rounded-xl bg-white px-3 py-2 text-sm font-medium text-slate-500 shadow-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

function MonthRow({ month, pct, onChanged }) {
  const [open, setOpen] = useState(false);
  const [giving, setGiving] = useState(false);
  const [busy, setBusy] = useState(false);

  const Line = ({ label, cents, minus }) => (
    <div className="flex items-baseline justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-600">
        {minus ? "−" : ""}
        {centsToDisplay(cents)}
      </span>
    </div>
  );

  return (
    <div>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-3 px-4 py-3 text-left">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800">
            {month.label}
            {month.open && (
              <span className="ml-2 rounded-full bg-navy-700/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-navy-700">
                still running
              </span>
            )}
          </p>
          <p className="text-xs text-slate-400">
            {centsToDisplay(month.base_cents)} after expenses and tax
            {month.given && month.method ? ` · ${month.method}` : ""}
          </p>
        </div>
        <p className={`shrink-0 text-sm font-semibold ${month.given ? "text-emerald-600" : "text-navy-700"}`}>
          {centsToDisplay(month.tithe_cents)}
        </p>
        <span className="shrink-0 text-slate-300">{open ? "▾" : "›"}</span>
      </button>

      {open && (
        <div className="space-y-1 border-t border-slate-100 px-4 py-3">
          <Line label="Income received" cents={month.income_cents} />
          {month.expenses_cents > 0 && <Line label="Expenses" cents={month.expenses_cents} minus />}
          {month.mileage_cents > 0 && <Line label={`Mileage (${month.mileage_miles} mi)`} cents={month.mileage_cents} minus />}
          {month.carried_in_cents > 0 && <Line label="Covering the previous month's shortfall" cents={month.carried_in_cents} minus />}
          {month.tax_cents > 0 && <Line label="Estimated tax on this month" cents={month.tax_cents} minus />}
          <div className="flex items-baseline justify-between border-t border-slate-100 pt-1 text-xs font-semibold">
            <span className="text-slate-600">What the business kept</span>
            <span className="text-slate-700">{centsToDisplay(month.base_cents)}</span>
          </div>
          {month.given && month.given_base_cents !== null && month.given_base_cents !== month.base_cents && (
            <p className="pt-1 text-[11px] text-slate-400">
              Given on {centsToDisplay(month.given_base_cents)} — the figure when it was marked given.
            </p>
          )}
        </div>
      )}

      {giving ? (
        <GiveForm
          month={month}
          pct={pct}
          onDone={() => {
            setGiving(false);
            onChanged();
          }}
          onCancel={() => setGiving(false)}
        />
      ) : (
        <div className="flex justify-end px-4 pb-3">
          {month.given ? (
            <button
              onClick={async () => {
                if (!window.confirm("Undo — mark this month as not yet given?")) return;
                setBusy(true);
                try {
                  await api("/api/tithe", { method: "POST", body: { action: "ungive", month_start: month.month_start } });
                  onChanged();
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold uppercase text-emerald-700"
            >
              Given ✓
            </button>
          ) : month.open ? (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase text-slate-400">open</span>
          ) : month.tithe_cents > 0 ? (
            <button onClick={() => setGiving(true)} className="rounded-xl bg-navy-700 px-3 py-1.5 text-xs font-semibold text-white">
              Mark given
            </button>
          ) : (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase text-slate-400">
              no profit
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function Tithe() {
  const [data, setData] = useState(null);
  const [editingPct, setEditingPct] = useState(false);
  const [pctDraft, setPctDraft] = useState("");

  const load = () => api("/api/tithe").then(setData).catch(() => setData({ pct: 10, months: [], ytd: null }));
  useEffect(() => {
    load();
  }, []);

  if (!data)
    return (
      <Shell title="Tithe" back>
        <p className="text-center text-sm text-slate-400">Loading…</p>
      </Shell>
    );

  return (
    <Shell title="Tithe" back>
      <section className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">This year</p>
            <p className="mt-1 text-2xl font-semibold text-navy-700">{data.ytd ? centsToDisplay(data.ytd.given_cents) : "—"}</p>
            <p className="text-xs text-slate-500">given of {data.ytd ? centsToDisplay(data.ytd.tithe_cents) : "—"}</p>
          </div>
          <div className="text-right">
            {data.ytd?.owed_cents > 0 && (
              <>
                <p className="text-lg font-semibold text-amber-600">{centsToDisplay(data.ytd.owed_cents)}</p>
                <p className="text-xs text-slate-500">to give</p>
              </>
            )}
            {data.ytd && data.ytd.owed_cents <= 0 && data.ytd.tithe_cents > 0 && (
              <p className="text-sm font-semibold text-emerald-600">All given ✓</p>
            )}
          </div>
        </div>
        <div className="mt-3 border-t border-slate-100 pt-2">
          {editingPct ? (
            <div className="flex items-center gap-2">
              <input value={pctDraft} onChange={(e) => setPctDraft(e.target.value)} inputMode="decimal" className={`${inputCls} w-24`} />
              <span className="text-sm text-slate-500">%</span>
              <button
                onClick={async () => {
                  await api("/api/tithe", { method: "POST", body: { action: "set_pct", pct: Number(pctDraft) } });
                  setEditingPct(false);
                  load();
                }}
                className="rounded-lg bg-navy-700 px-3 py-2 text-xs font-semibold text-white"
              >
                Save
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setPctDraft(String(data.pct));
                setEditingPct(true);
              }}
              className="text-left text-xs text-slate-400"
            >
              {data.pct}% of what the business keeps each month — income after expenses and estimated tax · change
            </button>
          )}
        </div>
      </section>

      {data.months.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-4xl">🙏</p>
          <p className="mt-3 font-medium text-slate-700">No months yet</p>
          <p className="mt-1 text-sm text-slate-500">As income and expenses land in the books, each month's tithe appears here.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
          {data.months.map((m) => (
            <MonthRow key={m.month_start} month={m} pct={data.pct} onChanged={load} />
          ))}
        </div>
      )}

      <p className="text-center text-xs text-slate-400">
        Tap a month to see the arithmetic. The current month stays open until it ends — expenses are still arriving.
        Tax is this year's estimate spread across the months that earned it, so it moves as the year fills in.
      </p>
    </Shell>
  );
}
